import Button from '@/components/Button';
import { font } from '@/theme/zeelool.theme';
import { LocaleType } from '@/utils/constants';
import { ECouponButtonType, ECouponType, ECouponUIType } from '@/utils/enums/coupon';
import { styled, Tooltip, tooltipClasses, TooltipProps, useMediaQuery } from '@mui/material';
import { CollapseArrowDownOutlined, CollapseArrowUpOutlined, QuestionFilled } from '@zeelool/icon';
import { cva } from 'class-variance-authority';
import { useLocale } from 'next-intl';
import { useTranslations } from '@/i18n/use-translations';
import { useState } from 'react';
import styles from './index.module.scss';
import { ICouponProps } from './type';

// Define variants for coupon container
const couponContainerVariants = cva(
  `${styles.couponContainer} flex notranslate min-h-[82px] sm:min-h-[92px] overflow-hidden  flex-col justify-between items-center`,
  {
    variants: {
      uiType: {
        [ECouponUIType.NORMAL]: '',
        [ECouponUIType.COUNT_DOWN]: styles.countDown,
        [ECouponUIType.DISABLED]: styles.disabled,
        [ECouponUIType.SHIPPING]: styles.shipping,
      },
    },
    defaultVariants: {
      uiType: ECouponUIType.NORMAL,
    },
  },
);

// Define variants for discount text
const discountTextVariants = cva(
  `font-be text-[20px] sm:text-[24px] leading-[20px] sm:leading-[24px] flex px-8 pr-16 justify-center items-center text-center tracking-[-1px] `,
  {
    variants: {
      uiType: {
        [ECouponUIType.NORMAL]: 'text-primary-text',
        [ECouponUIType.COUNT_DOWN]: 'text-primary-text',
        [ECouponUIType.DISABLED]: 'text-[#999999]',
        [ECouponUIType.SHIPPING]: 'text-primary-text',
      },
    },
    defaultVariants: {
      uiType: ECouponUIType.NORMAL,
    },
  },
);

// Define variants for title
const titleVariants = cva(`font-bb text-16`, {
  variants: {
    uiType: {
      [ECouponUIType.NORMAL]: 'text-primary-black',
      [ECouponUIType.COUNT_DOWN]: 'text-primary-black',
      [ECouponUIType.DISABLED]: 'text-[#999999]',
      [ECouponUIType.SHIPPING]: 'text-primary-black',
    },
  },
  defaultVariants: {
    uiType: ECouponUIType.NORMAL,
  },
});

/**
 * 优惠券组件
 * 根据不同状态展示不同样式的优惠券
 */
const Coupon: React.FC<ICouponProps> = ({
  id,
  couponUIType,
  disCountText,
  title,
  description,
  buttonText,
  copyWriting,
  onClick,
  couponType,
  isStairs,
  isPointsMall,
  extension_field,
  discountTextClassName,
  titleClassName,
  containerClassName,
  renderRightButton,
  errorMessage,
  buttonLoading,
  labelPosition = 'top-left',
}: ICouponProps) => {
  const isMobile = useMediaQuery('(max-width:700px)');
  const t = useTranslations('components.coupon');
  const locale = useLocale() as LocaleType;
  const [isOpen, setIsOpen] = useState(false);

  // 优惠券标签配置
  const couponLabel = extension_field?.coupon_label;

  // 是否显示标签：coupon_tag 为 2 或 3 时显示
  const shouldShowLabel = couponLabel?.coupon_tag === 2 || couponLabel?.coupon_tag === 3;

  // 按钮文本内容
  const buttonLabel = buttonText === ECouponButtonType.GET ? t('buttons.get') : t('buttons.shop');

  // 是否显示按钮
  const showButton = couponUIType !== ECouponUIType.DISABLED;

  const mobileFirstTagStyles = cva('text-white text-12 font-br', {
    variants: {
      locale: {
        en: 'leading-[12px] text-12',
        de: 'leading-[14px] text-12',
        es: 'leading-[14px] text-12',
        fr: 'leading-[12px] text-10',
        it: 'leading-[14px] text-12',
        pt: 'leading-[14px] text-12',
        fi: 'leading-[14px] text-12',
        nl: 'leading-[14px] text-12',
      },
    },
    defaultVariants: {
      locale: 'en',
    },
  });

  const buttonStyles = cva('h-[28px] !rounded-2xl', {
    variants: {
      locale: {
        de: 'w-[82px]',
        en: 'w-[64px]',
        es: 'w-[64px]',
        fr: 'w-[82px]',
        it: 'w-[64px]',
        pt: 'w-[64px]',
        fi: 'w-[64px]',
        nl: 'w-[64px]',
      },
    },
    defaultVariants: {
      locale: 'en',
    },
  });

  const CouponTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.white,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: '#666',
      maxWidth: 300,
      padding: '10px 15px',
      fontSize: '14px',
      lineHeight: '1.5',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      border: '1px solid #eee',
      borderRadius: '4px',
      fontFamily: font.br,
    },
  }));

  // 从description中提取shippingMethod
  // 示例格式: <div id="shipping-method">${shippingMethod}</div><div class="mt-8">${validDate}</div>
  const shippingMethod = description?.match(/<div id="shipping-method">(.*?)<\/div>/)?.[1];

  // TODO:是否过滤掉阶梯券
  if (isStairs) return;

  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        id={`coupon-${id}`}
        className={`${couponContainerVariants({ uiType: couponUIType })} ${containerClassName}`}
        key={id}
      >
        {/* 优惠券标签 */}
        {shouldShowLabel && couponLabel?.tag_text && (
          <div
            className={`absolute top-0 bg-[#000000] opacity-40 px-10 py-2 ${
              labelPosition === 'top-right'
                ? 'right-0 rounded-tr-[8px] rounded-bl-[8px]'
                : 'left-0 rounded-tl-[8px] rounded-br-[8px]'
            }`}
          >
            <div className={mobileFirstTagStyles({ locale })}>{couponLabel.tag_text}</div>
          </div>
        )}

        <div className="flex justify-between items-center w-full flex-1">
          {/* 左侧折扣区域 */}
          {(couponType === ECouponType.DISCOUNT || couponType === ECouponType.SHIPPING) && (
            <div
              className={`${discountTextVariants({ uiType: couponUIType })} ${discountTextClassName}`}
              dangerouslySetInnerHTML={{ __html: disCountText }}
              style={{
                letterSpacing: '-1px',
              }}
            />
          )}

          {/* 中间内容区域 */}
          <div className={`px-8 flex flex-col justify-start flex-1`}>
            <div
              className={`${titleVariants({ uiType: couponUIType })} ${titleClassName}`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            {description && !isPointsMall && (
              <div
                className={`font-br text-12 mt-6 sm:mt-8 text-auxiliary-text `}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
            {description && isPointsMall && shippingMethod && (
              <div
                className={`font-br text-12 mt-6 sm:mt-8 text-auxiliary-text `}
                dangerouslySetInnerHTML={{ __html: shippingMethod }}
              />
            )}

            {copyWriting && isMobile && !isPointsMall && (
              <div className="flex items-center  mt-6 sm:mt-8" onClick={() => setIsOpen(!isOpen)}>
                <div className="font-br text-12 text-auxiliary-text  mr-4">{t('labels.guide')}</div>

                {isOpen ? (
                  <CollapseArrowUpOutlined className="text-auxiliary-text text-14" />
                ) : (
                  <CollapseArrowDownOutlined className="text-auxiliary-text text-14" />
                )}
              </div>
            )}
          </div>

          {renderRightButton
            ? renderRightButton()
            : showButton &&
              !isPointsMall && (
                <div className={` flex justify-center items-center`}>
                  <Button
                    variant="contained"
                    color={'black'}
                    className={buttonStyles({ locale })}
                    onClick={() => onClick(id, buttonLabel, couponType)}
                    loading={buttonLoading}
                  >
                    {buttonLabel}
                  </Button>
                </div>
              )}

          {copyWriting && !isMobile && !isPointsMall && (
            <CouponTooltip
              title={<div dangerouslySetInnerHTML={{ __html: copyWriting }} />}
              placement="top"
              sx={{
                zIndex: 99,
              }}
            >
              <div className={styles.couponInfoIcon}>
                <QuestionFilled className="text-[#a5a9b2]" />
              </div>
            </CouponTooltip>
          )}
        </div>
        {errorMessage && (
          <div className="mt-[10px]  bg-[#eeeeee] whitespace-nowrap text-primary-text rounded-b-8 w-full overflow-hidden text-left justify-start text-14 md:text-xs">
            <div className="overflow-hidden border-t border-dashed border-[#d4d4d4] pt-4 mx-12 pb-4">
              {errorMessage}
            </div>
          </div>
        )}
      </div>

      {copyWriting && isMobile && isOpen && !isPointsMall && (
        <div className="bg-[#f4f4f4] px-12 py-8 text-[12px] pt-12 text-auxiliary-text leading-[14px] rounded-b-8 mt-[-4px]">
          <div dangerouslySetInnerHTML={{ __html: copyWriting }} />
        </div>
      )}
    </div>
  );
};

export default Coupon;
